import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { currencyFormatter } from "../../lib/utils";
import { useGetOrderQuery } from "../../store/api/orderApi";

const OrderModal = ({ isOpen, onClose, id }) => {
  const { data, isLoading, error } = useGetOrderQuery(id);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>View Order</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2">Loading data...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-destructive">
            Error loading order data. Please try again.
          </div>
        ) : (
          <div className="w-full mx-auto bg-white border border-gray-300 rounded-lg overflow-hidden">
            {/* Customer Information */}
            <div className="p-5 border-b border-gray-300">
              <h2 className="text-md text-blue-600 font-semibold mb-4">
                Customer Information
              </h2>

              <p>
                <span className="text-gray-500">Name: </span>
                <span className="text-gray-900">
                  {data?.project?.customer?.firstName || ""}{" "}
                  {data?.project?.customer?.lastName || ""}
                </span>
              </p>

              <p>
                <span className="text-gray-500">Email: </span>
                <span className="text-gray-900">
                  {data?.project?.customer?.email || ""}
                </span>
              </p>

              <p>
                <span className="text-gray-500">Phone: </span>
                <span className="text-gray-900">
                  {data?.project?.customer?.phone || ""}
                </span>
              </p>

              <p>
                <span className="text-gray-500">Organization: </span>
                <span className="text-gray-900">
                  {data?.project?.customer?.organization || ""}
                </span>
              </p>

              <p>
                <span className="text-gray-500">Address: </span>
                <span className="text-gray-900">
                  {data?.project?.customer?.organizationAddress || ""},{" "}
                  {data?.project?.customer?.city || ""},{" "}
                  {data?.project?.customer?.state || ""},{" "}
                  {data?.project?.customer?.country || ""} -{" "}
                  {data?.project?.customer?.postalCode || ""}
                </span>
              </p>
            </div>

            {/* Project Details */}
            <div className="p-5">
              <h2 className="text-md text-blue-600 font-semibold mb-4">
                Project Details
              </h2>

              <p>
                <span className="text-gray-500">Project ID: </span>
                <span className="text-gray-900">{data.project?.id || ""}</span>
              </p>

              <p>
                <span className="text-gray-500">Title: </span>
                <span className="text-gray-900">
                  {data.project?.title || ""}
                </span>
              </p>

              <p>
                <span className="text-gray-500">Description: </span>
                <span className="text-gray-900">
                  {data.project?.description || ""}
                </span>
              </p>

              <p>
                <span className="text-gray-500">Duration: </span>
                <span className="text-gray-900">
                  {data.project?.startDate || ""} to{" "}
                  {data.project?.endDate || ""}
                </span>
              </p>

              <p>
                <span className="text-gray-500">Priority: </span>
                <span className="text-gray-900">{data?.priority || ""}</span>
              </p>

              <p>
                <span className="text-gray-500">Subsidy: </span>
                <span className="text-gray-900">
                  {data?.subsidyPercentage || ""}% (Max:
                  {currencyFormatter(data?.subsidyMaxAmount) || ""})
                </span>
              </p>

              {(data.services || []).map((service, serviceIndex) => (
                <div id={serviceIndex}>
                  <h3 className="text-blue-600 font-semibold mt-6 mb-2 capitalize">
                    {service.type} Services
                  </h3>

                  <table className="w-full text-sm border border-gray-300 border-collapse">
                    <thead className="bg-gray-100">
                      <TableHeader service={service} />
                    </thead>
                    <tbody>
                      <TableRows service={service} />
                    </tbody>
                  </table>
                </div>
              ))}

              {/* Request + Source */}
              <p className="mt-4">
                <span className="text-gray-500">Request ID:</span>{" "}
                <span className="text-gray-900">{data?.requestId}</span>
              </p>

              <p className="mt-0">
                <span className="text-gray-500">Source:</span>{" "}
                <span className="text-gray-900">{data?.source}</span>
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const TableHeader = ({ service }) => {
  if (service.type === "compute") {
    return (
      <tr>
        <th className="p-2 text-left text-gray-600">ID</th>
        <th className="p-2 text-left text-gray-600">Quantity</th>
        <th className="p-2 text-left text-gray-600">Type</th>
        <th className="p-2 text-left text-gray-600">Max Hours</th>
        <th className="p-2 text-left text-gray-600">Storage</th>
      </tr>
    );
  }

  if (service.type === "storage") {
    return (
      <tr>
        <th className="p-2 text-left text-gray-600">Type</th>
        <th className="p-2 text-left text-gray-600">Quantity (GB)</th>
        <th className="p-2 text-left text-gray-600">Months</th>
      </tr>
    );
  }

  if (service.type === "platform") {
    return (
      <tr>
        <th className="p-2 text-left text-gray-600">Type</th>
        <th className="p-2 text-left text-gray-600">Discount (%)</th>
      </tr>
    );
  }
};

const TableRows = ({ service }) => {
  if (service.type === "compute") {
    return service?.config.map((item, itemIndex) => (
      <tr key={`${itemIndex}`} className="border-t border-gray-300">
        <td className="p-2 text-gray-900">{item?.id || ""}</td>
        <td className="p-2 text-gray-900">{item?.quantity || ""}</td>
        <td className="p-2 text-gray-900">{item?.subscriptionType || ""}</td>
        <td className="p-2 text-gray-900">{item?.maxHours || ""}</td>
        <td className="p-2 text-gray-900">{item?.Storage || ""}</td>
      </tr>
    ));
  }

  if (service.type === "storage") {
    return service?.config.map((item, itemIndex) => (
      <tr key={itemIndex} className="border-t border-gray-300">
        <td className="p-2 text-gray-900">{item?.type || ""}</td>
        <td className="p-2 text-gray-900">{item?.quantity || ""}</td>
        <td className="p-2 text-gray-900">{item?.months || ""}</td>
      </tr>
    ));
  }

  if (service.type === "platform") {
    return service?.config.map((item, itemIndex) => (
      <tr key={itemIndex} className="border-t border-gray-300">
        <td className="p-2 text-gray-900">{item?.type || ""}</td>
        <td className="p-2 text-gray-900">{item?.discountPer || ""}</td>
      </tr>
    ));
  }
};

export default OrderModal;
